import { injectable } from 'tsyringe';
import { IAIUsageProviderToken, IDirectoryProviderToken } from '@bmai/shared';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';
import { IntegrationsService } from '@/features/integrations/IntegrationsService';
import { BillingService, type BillingOverview } from '@/features/billing/BillingService';
import { UsersAdminService } from '@/features/admin/UsersAdminService';
import { NotificationsApiService } from '@/features/notifications/NotificationsApiService';
import type { JobRunEntry } from '@/features/notifications/NotificationTypes';

export interface OnboardingConnection {
    id: string;
    label: string;
}

/** Live signals that drive the onboarding checklist ticks and the final job set. */
export interface OnboardingSignals {
    providerConnections: OnboardingConnection[];
    directoryConnections: OnboardingConnection[];
    userCount: number;
    hasIngestedData: boolean;
    overview: BillingOverview | null;
}

export interface FirstRunJob {
    kind: string;
    jobRunId: string;
}

export interface FirstRunAck {
    correlationId: string;
    jobs: FirstRunJob[];
}

/**
 * Data access for the first-run onboarding flow. Reads the signals that tick the
 * checklist (provider + identity-provider connections, team size, ingested data,
 * billing) and fires the "bring your data to life" jobs (usage pipeline, license
 * + directory sync) that surface in the notification tray. Also persists the
 * dismiss/complete flag on the tenant.
 */
@injectable()
export class OnboardingService {
    constructor(
        private readonly integrations: IntegrationsService,
        private readonly billing: BillingService,
        private readonly users: UsersAdminService,
        private readonly notifications: NotificationsApiService,
    ) {}

    async loadSignals(): Promise<OnboardingSignals> {
        const [aiTypes, dirTypes, connections, ingestion, overview, userCount] = await Promise.all([
            this.integrations.listProviderTypes(IAIUsageProviderToken).catch(() => []),
            this.integrations.listProviderTypes(IDirectoryProviderToken).catch(() => []),
            this.integrations.listConnections().catch(() => []),
            this.integrations.fetchIngestionStatus().catch(() => []),
            this.billing.getOverview().catch(() => null),
            this.countUsers(),
        ]);
        const aiIds = new Set(aiTypes.map((t) => t.id));
        const dirIds = new Set(dirTypes.map((t) => t.id));
        const providerConnections = connections
            .filter((c) => aiIds.has(c.providerTypeId))
            .map((c) => ({ id: c.id, label: c.displayName || c.id }));
        const directoryConnections = connections
            .filter((c) => dirIds.has(c.providerTypeId))
            .map((c) => ({ id: c.id, label: c.displayName || c.id }));
        const hasIngestedData = ingestion.some((s) => s.recordsIngested > 0);
        return { providerConnections, directoryConnections, userCount, hasIngestedData, overview };
    }

    async startFirstRun(providerConnectionId: string): Promise<FirstRunAck> {
        const res = await authenticatedFetch('/_telemetry/api/telemetry/onboarding/first-run', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ providerConnectionId }),
        });
        if (!res.ok) throw new Error(`Failed to start first-run pipeline (${res.status})`);
        return (await res.json()) as FirstRunAck;
    }

    async startLicenseSync(connectionId: string): Promise<void> {
        await this.postSync(`/_licenses/api/licenses/pools/${encodeURIComponent(connectionId)}/sync`);
    }

    async startDirectorySync(connectionId: string): Promise<void> {
        await this.postSync(`/_directory/api/directory/connections/${encodeURIComponent(connectionId)}/sync`);
    }

    async listJobs(limit = 40): Promise<JobRunEntry[]> {
        const page = await this.notifications.listMyJobs(limit);
        return page.entries;
    }

    async dismiss(): Promise<void> {
        await this.patchOnboarding({ dismissed: true });
    }

    async complete(): Promise<void> {
        await this.patchOnboarding({ completedAt: new Date().toISOString() });
    }

    private async countUsers(): Promise<number> {
        try {
            return (await this.users.listUsers()).length;
        } catch {
            return 0;
        }
    }

    private async postSync(url: string): Promise<void> {
        const res = await authenticatedFetch(url, { method: 'POST' });
        if (!res.ok) throw new Error(`Sync request failed (${res.status})`);
    }

    private async patchOnboarding(onboarding: { dismissed?: boolean; completedAt?: string }): Promise<void> {
        const res = await authenticatedFetch('/_auth/api/tenants/current', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ onboarding }),
        });
        if (!res.ok) throw new Error(`Failed to update onboarding (${res.status})`);
    }
}
